import React from 'react';
import produce from 'immer';
import { Link } from 'react-router-dom';

import { IProps, IState } from './types';
import { ActionForAuthentication } from '../../types';

import toaster, { Theme } from '../../../../shared/toaster';
import Input from '../../../../shared/design-system/components/input';
import Button from '../../../../shared/design-system/components/atoms/button';

import AuthHeader from '../auth-header';
import AuthContent from '../auth-content';
import AuthFooter from '../auth-footer';
import OauthButtons from '../oauth-buttons';
import ButtonContainer from '../button-container';

import { validate } from './validator';
import { AnalyticsEvents } from '../../../../shared/enums/analytics';
import { getIsRequestPending } from '../../../../shared/utils/get-request-status';
import {
  AuthHelper,
  executeOnErrorWithErrorCheck,
  executeOnRequestStatusWithPrevStatusCheck,
  getCurrentToken,
  getGleapAnonymousUserId,
  getGleapUserHash,
  setGleapAnonymousUserId,
  setGleapAnonymousUserHash,
  setGleapUser,
  setGleapUserHash,
  initializeThirdPartyIntegrations,
} from '../../../../shared/utils';
import { capturePosthogEvent } from '../../../../shared/utils/initialize-third-party-integrations';
import {
  TRULYINBOX_V2_URL,
  TRULYINBOX_V2_REGISTER_URL,
} from '../../../../shared/utils/redirect-to-v2';

class Login extends React.PureComponent<IProps, IState> {
  private turnstileRef = React.createRef<HTMLDivElement>();

  private turnstileWidgetId: string | null = null;

  constructor(props: IProps) {
    super(props);

    this.state = {
      values: {
        email: '',
        password: '',
      },
      errors: {
        email: '',
        password: '',
      },
      dirty: {
        email: false,
        password: false,
      },
      showPassword: false,
      captchaToken: '',
      showV2MigrationLink: !!(props.location.state as
        | { showV2MigrationLink?: boolean }
        | undefined)?.showV2MigrationLink,
    };

    this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
  }

  componentDidMount() {
    const { history, sendGetAnonymousUserHashRequest } = this.props;
    const { values, showV2MigrationLink } = this.state;

    const { search } = history.location;

    // Drop the flag from history so the notice doesn't survive a refresh.
    if (showV2MigrationLink) {
      history.replace({ ...history.location, state: undefined });
    }
    const userEmail = new URLSearchParams(search).get('userEmail');

    if (userEmail !== '') {
      this.setState({ values: { ...values, email: userEmail } });
    }

    const isTokenPresent = !!getCurrentToken();
    const existingGleapUserHash = getGleapUserHash();
    const existingAnonymousUserId = getGleapAnonymousUserId();
    if (
      !isTokenPresent &&
      !(existingGleapUserHash && existingAnonymousUserId)
    ) {
      sendGetAnonymousUserHashRequest();
    }

    if ((window as any).turnstile) {
      this.renderTurnstile();
    } else {
      (window as any).onloadTurnstileCallback = () => this.renderTurnstile();
    }
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    const {
      token,
      trackingId,
      firstName,
      lastName,
      email,
      agencyConfig,

      loginStatus,
      loginError,
      showError,
      hideError,
      anonymousUserHashStatus,
      anonymousUserHash,
      anonymousUserId,
    } = this.props;

    executeOnRequestStatusWithPrevStatusCheck({
      status: loginStatus,
      prevStatus: prevProps.loginStatus,
      onSuccess: () => {
        if (token) {
          AuthHelper.login({ token });

          initializeThirdPartyIntegrations({
            trackingId,
            showChatSupport: agencyConfig?.showChatSupport,
            firstName,
            lastName,
            email,
          });

          capturePosthogEvent(AnalyticsEvents?.SignedIn, {
            'User Email': email,
            source: 'web_login_form',
          });
        }
      },
      onFailed: () => {
        if (this.turnstileWidgetId) {
          (window as any).turnstile?.reset(this.turnstileWidgetId);
        }
        this.setState({ captchaToken: '' });
        executeOnErrorWithErrorCheck({
          error: loginError,
          onError: () => {
            if (showError) {
              toaster.error(loginError.message, { theme: Theme.New });
              hideError();
            }
          },
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: anonymousUserHashStatus,
      prevStatus: prevProps.anonymousUserHashStatus,
      onSuccess: () => {
        if (anonymousUserHash && anonymousUserId) {
          setGleapUserHash(anonymousUserHash);
          setGleapAnonymousUserHash(anonymousUserHash);
          setGleapAnonymousUserId(anonymousUserId);
          setGleapUser({ shAccountId: '' });
        }
      },
    });
  }

  componentWillUnmount() {
    const { hideError } = this.props;
    hideError();
    if (this.turnstileWidgetId) {
      (window as any).turnstile?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }

  onInputChange(value, e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        draft.values[name] = value;
        draft.dirty[name] = true;
      }),
    );
  }

  onInputBlur(e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        if (draft.dirty[name]) {
          draft.errors[name] = validate(name, draft.values[name]);
        }
      }),
    );
  }

  onFormSubmit(e) {
    e.preventDefault();

    const { dirty, errors, values } = this.state;
    const { sendLoginRequest } = this.props;

    const dirtyRef = { ...dirty };
    const dirtyKeys = Object.keys(dirtyRef);

    dirtyKeys.forEach((key) => {
      dirtyRef[key] = true;
    });

    const errorsRef = { ...errors };
    const errorsKeys = Object.keys(errorsRef);
    let isError = false;

    errorsKeys.forEach((key) => {
      const error = validate(key, values[key]);

      errorsRef[key] = error;
      isError = isError || !!error;
    });

    this.setState({ errors: errorsRef, dirty: dirtyRef });

    if (isError) {
      return;
    }

    const { email, password } = values;
    const { captchaToken } = this.state;
    sendLoginRequest(email, password, captchaToken);
  }

  togglePasswordVisibility() {
    this.setState((state) => ({ showPassword: !state.showPassword }));
  }

  renderTurnstile() {
    if (
      this.turnstileRef.current &&
      (window as any).turnstile &&
      !this.turnstileWidgetId
    ) {
      this.turnstileWidgetId = (window as any).turnstile.render(
        this.turnstileRef.current,
        {
          sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
          callback: (token: string) => this.setState({ captchaToken: token }),
          'expired-callback': () => this.setState({ captchaToken: '' }),
          'error-callback': () => this.setState({ captchaToken: '' }),
        },
      );
    }
  }

  render() {
    const {
      values,
      errors,
      showPassword,
      captchaToken,
      showV2MigrationLink,
    } = this.state;
    const { loginStatus } = this.props;

    const isLoading = getIsRequestPending(loginStatus);

    return (
      <>
        <AuthHeader title="Login" />

        <AuthContent>
          {showV2MigrationLink && (
            <div className="auth-v2-migration-notice">
              <p className="auth-v2-migration-notice__text">
                We have moved to the new TrulyInbox V2. Please create a new
                account there.
              </p>
              <a
                className="auth-v2-migration-notice__link"
                href={TRULYINBOX_V2_URL}
              >
                Go to TrulyInbox V2
              </a>
            </div>
          )}

          <OauthButtons type={ActionForAuthentication.Login} />

          <form className="auth-form less-spacing" onSubmit={this.onFormSubmit}>
            <Input
              name="email"
              label="Email"
              placeholder="Enter your email address"
              value={values.email}
              variant={errors.email && Input.Variant.Error}
              caption={errors.email}
              onChange={this.onInputChange}
              onBlur={this.onInputBlur}
              autoFocus
            />

            <div className="password-and-forgot-password-container">
              <Input
                name="password"
                label="Password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                variant={errors.password && Input.Variant.Error}
                caption={errors.password}
                onChange={this.onInputChange}
                onBlur={this.onInputBlur}
                icons={[
                  {
                    place: Input.IconPlace.Right,
                    identifier: showPassword ? 'eye-alt' : 'eye',
                    className: 'pointer',
                    onClick: this.togglePasswordVisibility,
                  },
                ]}
                className="with-icon-right"
              />

              <div className="forgot-password-link">
                <Link to="/reset-password" className="pointer">
                  Forgot Password?
                </Link>
              </div>
            </div>

            <div ref={this.turnstileRef} />

            <ButtonContainer className="mt-12px">
              <Button
                variant={Button.Variant.Primary}
                type={Button.Type.Submit}
                isLoading={isLoading}
                disabled={
                  !(!!values.email && !!values.password && !!captchaToken) ||
                  isLoading
                }
                className="auth-action-btn"
              >
                Login
              </Button>
            </ButtonContainer>
          </form>
        </AuthContent>

        <AuthFooter>
          <p>
            Need an account?{' '}
            <a href={TRULYINBOX_V2_REGISTER_URL}>
              Create an account in Trulyinbox V2
            </a>
          </p>
        </AuthFooter>
      </>
    );
  }
}

export default Login;
